import classNames from 'classnames';
import { useContext } from 'react';
import { Outlet } from 'react-router-dom';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import Wordmark from '@/components/Wordmark';
import useBrandName from '@/hooks/use-brand-name';
import usePlatform from '@/hooks/use-platform';
import { getBrandingLogoUrl } from '@/shared/utils/logo';
import { layoutClassNames } from '@/utils/consts';

import CustomContent from './CustomContent';
import TopBar from './TopBar';
import styles from './index.module.scss';

const AppLayout = () => {
  const { isMobile } = usePlatform();
  const { experienceSettings, theme } = useContext(PageContext);
  const brandName = useBrandName();
  const logo = getBrandingLogoUrl({
    theme,
    branding: experienceSettings?.branding ?? {},
    isDarkModeEnabled: experienceSettings?.color.isDarkModeEnabled ?? false,
  });

  return (
    <div className={styles.viewBox}>
      {/* TDA: language switcher and theme toggle. The brand is below, in the card. */}
      <TopBar />
      <div className={classNames(styles.container, layoutClassNames.pageContainer)}>
        {!isMobile && <CustomContent className={layoutClassNames.customContent} />}
        <main className={classNames(styles.main, layoutClassNames.mainContent)}>
          {/*
           * TDA: the brand lockup, drawn ONCE for every screen.
           *
           * It used to live in two places — `TopBar` for the screens with no
           * card header and `BrandingHeader` for the four that have one — with
           * a `:has()` rule hiding whichever was redundant. That put the brand
           * in the page corner on `/unknown-session`, forgot password,
           * verification code, MFA and SSO email, and inside the card only on
           * sign-in, register, consent and device. One product, two positions.
           *
           * Here it is above `<Outlet />`, so every card opens with the same
           * lockup in the same place, which is what the sign-in card looks
           * like. `BrandingHeader` keeps only the tagline and headline — and
           * the third-party row on consent, where our logo is already part of
           * "their app ⟷ us" and this lockup hides itself.
           */}
          <div className={styles.brand}>
            {logo && <img alt={brandName} className={styles.brandLogo} src={logo} />}
            <Wordmark />
          </div>
          <Outlet />
          {/*
           * TDA: the "Powered by Logto" signature is not rendered.
           *
           * ⚠️ Not a preference — there is no configuration for it here. The
           * `hideLogtoBranding` setting exists, but `routes/sign-in-experience`
           * asserts `EnvSet.values.isCloud` before accepting it, so a
           * self-hosted tenant gets `request.invalid_input` no matter what it
           * sends. Measured, not assumed.
           *
           * ⚠️ For 法務: MPL-2.0 requires the LICENSE and the source notices to
           * survive (they do, untouched), and has no clause requiring an
           * attribution mark in the running UI. Recorded here so the decision
           * is reviewable rather than buried in a diff.
           */}
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
